/**
 * WallOfFame — Public viral surface for Bill Detective.
 *
 * Route: /wall-of-fame  (no auth)
 *
 * Layers:
 *   1. Running global counter ("$X saved · Y companies · Z engineers")
 *   2. Opt-in customer logo grid (with $ saved per company)
 *   3. Top-saver engineers leaderboard (alias-respecting)
 *   4. CTAs to add your own company + shareable PNG links
 *
 * Anchored on real /api/wall-of-fame/* reads. When no opt-in customers
 * exist yet, the page gracefully shows a "Be the first" empty-state
 * with a clear CTA to /pricing/contract.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Trophy, Sparkles, Share2, ArrowRight, Building2, Users,
  TrendingDown, Search,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUsd = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${Math.round(v).toLocaleString()}`;
};

export default function WallOfFame() {
  const [total, setTotal]         = useState(null);
  const [customers, setCustomers] = useState([]);
  const [leaders, setLeaders]     = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/wall-of-fame/total`).then((r) => r.data),
      axios.get(`${API}/wall-of-fame/customers`).then((r) => r.data?.customers || []),
      axios.get(`${API}/wall-of-fame/leaderboard?limit=15`).then((r) => r.data?.engineers || []),
    ]).then(([t, c, l]) => {
      setTotal(t); setCustomers(c); setLeaders(l);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totalCardUrl =
    `${process.env.REACT_APP_BACKEND_URL}/api/wall-of-fame/card.png?kind=total`;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <SEOHead
        title="Wall of Fame — Verified team savings on cloud + AI"
        description={
          total
            ? `${fmtUsd(total.total_saved_usd)} saved across ${total.company_count} companies and ${total.engineer_count} engineers. Verified by CARTIEAI Bill Detective.`
            : 'The most transparent FinOps wall on the internet. Every saved $ is tied to the engineer, the commit, and the PR. Verified by CARTIEAI.'
        }
        image={totalCardUrl}
      />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden border-b border-slate-900">
        <div className="absolute inset-0 pointer-events-none opacity-30"
             style={{ backgroundImage:
              'radial-gradient(circle at 30% 20%, rgba(16,185,129,0.12) 0%, transparent 50%), ' +
              'radial-gradient(circle at 70% 80%, rgba(16,185,129,0.06) 0%, transparent 50%)',
             }} />
        <div className="max-w-6xl mx-auto px-6 pt-20 pb-16 relative">
          <Link to="/" className="inline-flex items-center text-xs text-slate-400 hover:text-slate-100 mb-6">
            <ArrowRight className="w-3 h-3 mr-1 rotate-180" /> CARTIEAI · home
          </Link>

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 ring-1 ring-emerald-500/30 text-emerald-300 text-xs font-medium mb-6"
               data-testid="wof-engine-badge">
            <Sparkles className="w-3 h-3" /> Powered by the Bill Detective Engine
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight" data-testid="wof-hero">
            <span className="text-slate-100">Wall of </span>
            <span className="text-emerald-300">$ saved</span>
          </h1>

          {total && (
            <div className="mt-10 inline-block">
              <div className="text-7xl sm:text-8xl font-mono font-medium text-emerald-300" data-testid="wof-total">
                {fmtUsd(total.total_saved_usd)}
              </div>
              <div className="text-base text-slate-400 mt-2" data-testid="wof-total-sub">
                saved across{' '}
                <span className="text-slate-200 font-semibold">{total.company_count}</span>{' '}
                {total.company_count === 1 ? 'company' : 'companies'} ·{' '}
                <span className="text-slate-200 font-semibold">{total.engineer_count}</span>{' '}
                engineers · verified by CARTIE
              </div>
            </div>
          )}

          <p className="text-lg text-slate-400 mt-10 max-w-2xl">
            Every dollar on this wall is tied to a specific engineer, a specific
            commit, and a specific PR. No vanity metrics. No marketing math.
            Verified by CARTIE&apos;s nightly cloud-bill reconciliation.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/pricing/contract"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-medium transition"
              data-testid="wof-cta-add-company"
            >
              Add your company <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={totalCardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-3 rounded-full bg-slate-800/80 hover:bg-slate-800 ring-1 ring-slate-700 text-slate-200 text-sm font-medium transition"
              data-testid="wof-cta-share-png"
            >
              <Share2 className="w-4 h-4" /> Share this wall (PNG)
            </a>
          </div>
        </div>
      </section>

      {/* ── Million-dollar club + customer logos ── */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-medium">
            <Building2 className="w-5 h-5 inline mr-2 text-emerald-300" />
            Companies on the wall
          </h2>
          {loading && <span className="text-xs text-slate-500">loading…</span>}
        </div>

        {customers.length === 0 ? (
          <Card className="bg-slate-900/40 ring-1 ring-slate-800" data-testid="wof-empty-customers">
            <CardContent className="p-10 text-center">
              <Search className="w-10 h-10 mx-auto text-slate-600 mb-3" />
              <div className="text-slate-200 font-medium mb-1">Be the first on the wall.</div>
              <p className="text-sm text-slate-400 max-w-md mx-auto">
                Opt in from Settings → Wall of Fame after you connect a repo + cloud
                account. Every saved $ becomes a verifiable line item with your name
                on it.
              </p>
              <Link
                to="/pricing/contract"
                className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950 text-sm font-medium"
              >
                Get started <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wof-customers-grid">
            {customers.map((c) => {
              const cardInner = (
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    {c.company_logo_url ? (
                      <img src={c.company_logo_url} alt={c.company_name}
                           className="h-7 w-auto" loading="lazy" />
                    ) : (
                      <div className="text-base font-medium text-slate-100">{c.company_name}</div>
                    )}
                    {c.is_million_club && (
                      <Trophy className="w-4 h-4 text-emerald-300" aria-label="$1M+ club" />
                    )}
                  </div>
                  <div className="text-3xl font-mono text-emerald-300">{fmtUsd(c.total_saved_usd)}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {c.engineer_count} engineer{c.engineer_count === 1 ? '' : 's'} ·{' '}
                    {c.commits_count} commits
                  </div>
                  {c.industry && (
                    <div className="text-[11px] text-slate-500 mt-1">{c.industry}</div>
                  )}
                  <div className="text-[11px] text-emerald-400/80 hover:text-emerald-300 mt-3 inline-flex items-center gap-1">
                    Read the story <ArrowRight className="w-3 h-3" />
                  </div>
                </CardContent>
              );
              return c.slug ? (
                <Link key={c.owner_user_id} to={`/wall-of-fame/c/${c.slug}`}
                      data-testid={`wof-customer-${c.owner_user_id}`}>
                  <Card className={`${c.is_million_club ? 'ring-emerald-500/40' : 'ring-slate-800'} bg-slate-900/40 ring-1 hover:ring-emerald-500/40 transition cursor-pointer h-full`}>
                    {cardInner}
                  </Card>
                </Link>
              ) : (
                <Card key={c.owner_user_id}
                      className={`${c.is_million_club ? 'ring-emerald-500/40' : 'ring-slate-800'} bg-slate-900/40 ring-1 hover:ring-emerald-500/40 transition`}
                      data-testid={`wof-customer-${c.owner_user_id}`}>
                  {cardInner}
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Engineer leaderboard ── */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-t border-slate-900">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-medium">
            <Users className="w-5 h-5 inline mr-2 text-emerald-300" />
            Top-saver engineers
          </h2>
          <span className="text-xs text-slate-500">aliased by each company&apos;s preference</span>
        </div>

        {leaders.length === 0 ? (
          <Card className="bg-slate-900/40 ring-1 ring-slate-800" data-testid="wof-empty-leaders">
            <CardContent className="p-8 text-center text-slate-500">
              Once opt-in customers ship saving commits, top engineers will appear here.
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-slate-900/40 ring-1 ring-slate-800" data-testid="wof-leaders">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-slate-400 border-b border-slate-800">
                  <tr>
                    <th className="text-left p-4 font-medium">#</th>
                    <th className="text-left p-4 font-medium">Engineer</th>
                    <th className="text-left p-4 font-medium">Company</th>
                    <th className="text-right p-4 font-medium">Saved /mo</th>
                    <th className="text-right p-4 font-medium">Commits</th>
                    <th className="text-right p-4 font-medium">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {leaders.map((e) => {
                    const cardUrl =
                      `${process.env.REACT_APP_BACKEND_URL}/api/wall-of-fame/card.png?` +
                      `kind=engineer&label=${encodeURIComponent(e.engineer_alias)}` +
                      `&value_usd=${e.saved_usd_monthly}` +
                      `&subtitle=${encodeURIComponent(e.company_name)}`;
                    return (
                      <tr key={`${e.rank}-${e.engineer_alias}`}
                          className="border-b border-slate-800/60 hover:bg-slate-800/30"
                          data-testid={`wof-leader-${e.rank}`}>
                        <td className="p-4 text-slate-500 font-mono">{e.rank}</td>
                        <td className="p-4 text-slate-100">{e.engineer_alias}</td>
                        <td className="p-4 text-slate-300">{e.company_name}</td>
                        <td className="p-4 text-right text-emerald-300 font-mono">
                          <TrendingDown className="w-3 h-3 inline mr-1" />
                          {fmtUsd(e.saved_usd_monthly)}
                        </td>
                        <td className="p-4 text-right text-slate-400 font-mono">{e.commits_count}</td>
                        <td className="p-4 text-right">
                          <a href={cardUrl} target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200"
                             data-testid={`wof-leader-share-${e.rank}`}>
                            <Share2 className="w-3 h-3" /> Card
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      {/* ── How it works ── */}
      <section className="border-t border-slate-900 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-medium mb-6">How the wall stays honest</h2>
          <div className="grid md:grid-cols-3 gap-6 text-sm text-slate-400">
            <Card className="bg-slate-900/40 ring-1 ring-slate-800">
              <CardContent className="p-5">
                <div className="text-xs text-emerald-300 font-mono mb-1">1. ATTRIBUTE</div>
                Every saved $ is tied to a specific commit, PR, and engineer by our GitHub webhook + the Bill Detective engine pipeline.
              </CardContent>
            </Card>
            <Card className="bg-slate-900/40 ring-1 ring-slate-800">
              <CardContent className="p-5">
                <div className="text-xs text-emerald-300 font-mono mb-1">2. RECONCILE</div>
                Nightly we diff attributed savings against the actual AWS / Azure / GCP bill. Any commit that doesn&apos;t bear out in the bill drops off.
              </CardContent>
            </Card>
            <Card className="bg-slate-900/40 ring-1 ring-slate-800">
              <CardContent className="p-5">
                <div className="text-xs text-emerald-300 font-mono mb-1">3. PUBLISH</div>
                Customers opt in explicitly. Engineer names are aliased per the company&apos;s preference (first-initial / first-name / anonymous).
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ── Footer CTA ── */}
      <section className="border-t border-slate-900 py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-medium mb-3">Want your team on this wall?</h3>
          <p className="text-slate-400 mb-6">
            $0 upfront. 25% of verified savings. 3× ROI guarantee. We never delete anything
            without an explicit human click + email 2FA.
          </p>
          <Link
            to="/pricing/contract"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-medium"
            data-testid="wof-footer-cta"
          >
            Generate the ROI contract <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
